// hooks/useTutorialPrompt.js
import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useConversation } from './useConversation'
import { useAuthState } from './useAuthState'
import TutorialService from '../services/TutorialService'
import UserService from '../services/UserService'

const NEW_USER_DAYS_THRESHOLD = 7 // Account muss jünger als 7 Tage sein

export function useTutorialPrompt () {
  const { user } = useAuth()
  const { companyId } = useAuthState()
  const { conversations, hasConversations, loading: conversationsLoading } = useConversation()
  
  const [tutorialPreferences, setTutorialPreferences] = useState(null)
  const [loading, setLoading] = useState(true)
  const [shouldShowPrompt, setShouldShowPrompt] = useState(false)

  // Lade Tutorial-Präferenzen aus Firestore
  const loadTutorialPreferences = useCallback(async () => {
    if (!user || !companyId) {
      setLoading(false)
      return
    }

    try {
      const preferences = await TutorialService.getTutorialPreferences(
        user.uid,
        companyId
      )
      setTutorialPreferences(preferences)
    } catch (error) {
      console.error('Error loading tutorial preferences:', error)
      setTutorialPreferences(null)
    } finally {
      setLoading(false)
    }
  }, [user, companyId])

  // Prüfe ob User neu ist (Account < 7 Tage alt)
  const isNewUser = useCallback(() => {
    if (!user?.userData?.createdAt) {
      return false // Kein createdAt = nicht als neu behandeln
    }

    try {
      const createdAt = user.userData.createdAt?.toDate
        ? user.userData.createdAt.toDate()
        : new Date(user.userData.createdAt)

      const daysSinceCreation =
        (Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24)

      return daysSinceCreation < NEW_USER_DAYS_THRESHOLD
    } catch (error) {
      console.error('Error checking user age:', error)
      return false
    }
  }, [user])

  // Prüfe ob Prompt angezeigt werden soll
  useEffect(() => {
    if (loading || conversationsLoading || !user || !companyId) {
      setShouldShowPrompt(false)
      return
    }

    // Wenn User bereits Prompt abgelehnt hat oder Tutorial abgeschlossen hat
    if (tutorialPreferences?.hasSeenPrompt || tutorialPreferences?.hasCompletedTutorial) {
      setShouldShowPrompt(false)
      return
    }

    // Prüfe ob User neu ist
    const userIsNew = isNewUser()

    // Prüfe ob User keine Conversations hat
    const hasNoConversations = !hasConversations

    // Zeige Prompt nur wenn:
    // 1. User neu ist (Account < 7 Tage)
    // 2. User keine Conversations hat
    // 3. User Prompt noch nicht gesehen hat
    if (userIsNew && hasNoConversations) {
      setShouldShowPrompt(true)
    } else {
      setShouldShowPrompt(false)
    }
  }, [
    loading,
    conversationsLoading,
    user,
    companyId,
    tutorialPreferences,
    hasConversations,
    isNewUser
  ])

  // Lade Präferenzen beim Mount und wenn sich User/Company ändert
  useEffect(() => {
    loadTutorialPreferences()
  }, [loadTutorialPreferences])

  // Dismiss Prompt (Nicht mehr anzeigen)
  const dismissPrompt = useCallback(async () => {
    if (!user || !companyId) return

    try {
      await TutorialService.dismissTutorialPrompt(user.uid, companyId)
      await loadTutorialPreferences() // Aktualisiere lokalen State
    } catch (error) {
      console.error('Error dismissing prompt:', error)
    }
  }, [user, companyId, loadTutorialPreferences])

  // Markiere Tutorial als abgeschlossen
  const markTutorialCompleted = useCallback(async () => {
    if (!user || !companyId) return

    try {
      await TutorialService.markTutorialCompleted(user.uid, companyId)
      await loadTutorialPreferences() // Aktualisiere lokalen State
    } catch (error) {
      console.error('Error marking tutorial as completed:', error)
    }
  }, [user, companyId, loadTutorialPreferences])

  return {
    shouldShowPrompt,
    loading: loading || conversationsLoading,
    dismissPrompt,
    markTutorialCompleted,
    tutorialPreferences
  }
}

